import React, { useState } from 'react';
import { Table, Button, Pagination, Form, InputGroup } from 'react-bootstrap';

const MessageResultsTable = ({
  messages,
  totalPages,
  currentPage,
  pageSize,
  sort,
  sortOrder,
  filter,
  onPageChange,
  onSortChange,
  onFilterChange,
  onPageSizeChange,
  mode // 'case_attribution' or 'message_classification'
}) => {
  const [expandedRows, setExpandedRows] = useState({});

  console.log('Rendering MessageResultsTable with mode:', mode);
  console.log('Messages:', messages);
  
  // Toggle row expansion
  const toggleRowExpand = (messageId) => {
    setExpandedRows(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };
  
  // Handle filter change
  const handleFilterChange = (field, value) => {
    onFilterChange({ ...filter, [field]: value });
  };
  
  // Handle sort change
  const handleSortChange = (field) => {
    if (sort === field) {
      onSortChange(field, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(field, 'asc');
    }
  };
  
  // Render pagination controls
  const renderPagination = () => {
    const pages = [];
    
    // First page
    pages.push(
      <Pagination.First 
        key="first" 
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
      />
    );
    
    // Previous page
    pages.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );
    
    // Page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);
    
    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <Pagination.Item 
          key={page} 
          active={page === currentPage}
          onClick={() => onPageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    
    // Next page
    pages.push(
      <Pagination.Next 
        key="next" 
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    
    // Last page
    pages.push(
      <Pagination.Last 
        key="last" 
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
      />
    );
    
    return (
      <div className="d-flex justify-content-between align-items-center my-3">
        <div>
          <Form.Select 
            size="sm" 
            style={{ width: '80px' }} 
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Form.Select>
        </div>
        <Pagination>{pages}</Pagination>
      </div>
    );
  };
  
  // Truncate text for display
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  // Column header with sort indicator
  const renderSortableHeader = (label, field) => {
    const isSorted = sort === field;
    return (
      <th 
        className="sortable-header" 
        onClick={() => handleSortChange(field)}
        style={{ cursor: 'pointer' }}
      >
        {label}
        {isSorted && (
          <span className="ms-1">
            {sortOrder === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </th>
    );
  };
  
  return (
    <div className="message-results-table">
      {/* Filter row */}
      <div className="mb-3">
        <InputGroup size="sm">
          <InputGroup.Text>Filter Subject</InputGroup.Text>
          <Form.Control
            placeholder="Filter by subject..."
            value={filter?.message_subject || ''}
            onChange={(e) => handleFilterChange('message_subject', e.target.value)}
          />
        </InputGroup>
      </div>
      
      {/* Table */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th style={{ width: '50px' }}></th>
            {renderSortableHeader('Message ID', 'message_id')}
            {renderSortableHeader('Time', 'message_ts')}
            {renderSortableHeader('Subject', 'message_subject')}
            {renderSortableHeader('Direction', 'message_direction')}
            {renderSortableHeader('Sender', 'message_sender')}
            {renderSortableHeader('Recipient', 'message_recipient')}
            {mode === 'case_attribution' ? (
              <>
                {renderSortableHeader('Case ID', 'message_case_id')}
                <th>Confidence</th>
              </>
            ) : (
              <>
                {/* <th>Case ID</th> */}
                {renderSortableHeader('Case ID', 'message_case_id')}
                {renderSortableHeader('Activity', 'activity')}
                <th>Confidence</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {messages.length === 0 ? (
            <tr>
              <td colSpan={mode === 'case_attribution' ? 9 : 10} className="text-center">
                No messages found
              </td>
            </tr>
          ) : (
            messages.map((message) => {
              const isExpanded = expandedRows[message.message_id];
              return (
                <React.Fragment key={message.message_id}>
                  <tr>
                    <td>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => toggleRowExpand(message.message_id)}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </Button>
                    </td>
                    <td>{message.message_id}</td>
                    <td>{new Date(message.message_ts + 'Z').toLocaleString()}</td>
                    <td>{truncateText(message.message_subject, 50)}</td>
                    <td>{message.message_direction}</td>
                    <td>{truncateText(message.message_sender, 20)}</td>
                    <td>{truncateText(message.message_recipient, 20)}</td>
                    {mode === 'case_attribution' ? (
                      <>
                        <td>{message.message_case_id || 'N/A'}</td>
                        <td>{message.confidence ? `${(message.confidence * 100).toFixed(1)}%` : 'N/A'}</td>
                      </>
                    ) : (
                      <>
                        <td>{message.message_case_id || 'N/A'}</td>
                        <td>{message.activity || 'N/A'}</td>
                        <td>{message.confidence ? `${(message.confidence * 100).toFixed(1)}%` : 'N/A'}</td>
                      </>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr className="expanded-content">
                      <td colSpan={mode === 'case_attribution' ? 9 : 9}>
                        <div className="p-3">
                          <h6>Message Body</h6>
                          <pre style={{ 
                            whiteSpace: 'pre-wrap', 
                            maxHeight: '300px', 
                            overflow: 'auto',
                            backgroundColor: '#f8f9fa',
                            padding: '10px',
                            borderRadius: '4px'
                          }}>
                            {message.message_body}
                          </pre>
                          
                          <h6 className="mt-3">
                            {mode === 'case_attribution' 
                              ? 'Case Attribution Reason' 
                              : 'Classification Reason'}
                          </h6>
                          <pre style={{ 
                            whiteSpace: 'pre-wrap',
                            backgroundColor: '#f8f9fa', 
                            padding: '10px',
                            borderRadius: '4px'
                          }}>
                            {mode === 'case_attribution' 
                              ? (message.case_attribution_reason || 'No reason provided') 
                              : (message.message_classification_reason || 'No reason provided')}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </Table>
      
      {/* Pagination */}
      {totalPages > 0 && renderPagination()}
    </div>
  );
};

export default MessageResultsTable;